import { useEffect, useRef, useState } from 'react';
import type { ModuleInstance } from '@one/module-sdk';
import { Button, Notice } from '@one/ui';
import { loadModule } from './loader';
import {
  API_BASE,
  apiFetch,
  buildModuleContext,
  type ModuleHandle,
  type ShellServices,
} from './context';
import type { ManifestEntry } from './types';
import { modulStandAus, neueVersion } from './version';
import {
  apiBasis,
  bannerText,
  geladenerEintrag,
  nichtsLaden,
  umschalterText,
  vorschauAnfangs,
  vorschauVon,
} from './vorschau';

/** Wie oft die Shell nachsieht, ob das Backend eine andere Modulversion geladen hat (P10). */
const VERSION_ABFRAGE_MS = 60_000;

/**
 * Hat die API das Modul inzwischen ohne Neustart getauscht (ADR-010, Nachtrag P10)? Fragt das
 * öffentliche `/core/health` (ohne Token, nur die Kennung `modulStand`) bei sichtbarem Dokument
 * und beim Zurückkehren in den Tab. Ändert sich die Kennung, liest die Shell das angemeldete
 * Bootstrap und vergleicht die Version des geöffneten Moduls. Liefert die neue Version oder `null`.
 */
function useNeueVersion(entry: ManifestEntry, mandant: string): string | null {
  const [neu, setNeu] = useState<string | null>(null);
  useEffect(() => {
    setNeu(null);
    if (!entry.version) return;
    let aus = false;
    let bekannt: string | null = null;
    const pruefen = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const r = await fetch(`${API_BASE}/core/health`, { cache: 'no-store' });
        if (!r.ok || aus) return;
        const stand = modulStandAus(await r.json());
        if (!stand || stand === bekannt) return;
        const erster = bekannt === null;
        bekannt = stand;
        if (erster) return; // Ausgangsstand merken; die geöffnete Version ist aktuell.
        const b = await apiFetch('core/bootstrap', undefined, mandant);
        if (!b.ok || aus) return;
        const daten = (await b.json()) as { modules?: unknown };
        setNeu(neueVersion(entry, daten.modules));
      } catch {
        // Nicht erreichbar: kein Hinweis (die Shell zeigt dafür eigene Zustände).
      }
    };
    void pruefen();
    const takt = setInterval(() => void pruefen(), VERSION_ABFRAGE_MS);
    const sichtbar = () => void pruefen();
    document.addEventListener('visibilitychange', sichtbar);
    return () => {
      aus = true;
      clearInterval(takt);
      document.removeEventListener('visibilitychange', sichtbar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, entry.version, mandant]);
  return neu;
}

/** Ein Sprung an eine Stelle im Modul; `nr` macht denselben Pfad wiederholbar. */
export interface Sprungziel {
  pfad: string;
  nr: number;
}

/**
 * Mountet ein Laufzeit-Modul in die Shell — mit Fehlerkachel statt weißem
 * Bildschirm, wenn Laden, Contract-Check oder Mount scheitern.
 */
export function ModuleHost({
  entry,
  services,
  ziel,
}: {
  entry: ManifestEntry;
  services: ShellServices;
  /**
   * Wohin **im** Modul gesprungen werden soll — aus der Sidebar, einem Hinweis
   * der Startseite oder der Befehlspalette. Der Sprung geht über
   * `ModuleInstance.onNavigate` und `ctx.navigation.onNavigate`; das Modul bleibt
   * dabei gemountet. Ein Modul, das keines von beiden anbietet, bleibt einfach
   * auf seiner Seite stehen — das ist ein kleinerer Verlust als ein Fehler.
   */
  ziel?: Sprungziel;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const instanz = useRef<{ instance: ModuleInstance; handle: ModuleHandle } | null>(null);
  const zielRef = useRef(ziel);
  zielRef.current = ziel;
  const angewandt = useRef<number | null>(null);
  const neu = useNeueVersion(entry, services.activeTenant.slug);
  // Modulvorschau (P11): nur für Tester; die API entscheidet bei jeder Anfrage.
  const vorschau = vorschauVon(entry);
  const [vorschauAn, setVorschauAn] = useState(() => vorschauAnfangs(entry));
  useEffect(() => {
    setVorschauAn(vorschauAnfangs(entry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, vorschau?.version]);
  const geladen = geladenerEintrag(entry, vorschauAn);
  const basis = apiBasis(entry, vorschauAn);

  function springen(pfad: string) {
    const aktuell = instanz.current;
    if (!aktuell) return;
    try {
      aktuell.instance.onNavigate?.(pfad);
      aktuell.handle.notifyNavigate(pfad);
    } catch (err) {
      // Ein Modul, das mit dem Ziel nichts anfangen kann, soll trotzdem
      // stehen bleiben — offen auf seiner Startseite ist besser als weg.
      console.error(`[shell] onNavigate '${pfad}' in '${entry.id}':`, err);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // Modul nur als Vorschau: erst nach dem Klick „Vorschau … testen“ laden.
    if (nichtsLaden(entry, vorschauAn)) {
      if (containerRef.current) containerRef.current.innerHTML = '';
      return;
    }

    loadModule(geladen)
      .then((mod) => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = '';
        const handle = buildModuleContext(geladen, services, { apiBasis: basis });
        const instance = mod.mount(containerRef.current, handle.ctx);
        instanz.current = { instance, handle };
        const z = zielRef.current;
        if (z) {
          angewandt.current = z.nr;
          springen(z.pfad);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
      try {
        instanz.current?.instance.unmount();
      } catch (err) {
        console.error(`[shell] unmount '${entry.id}' fehlgeschlagen:`, err);
      }
      instanz.current = null;
      angewandt.current = null;
    };
    // Neu mounten nur bei Modul- oder Mandantenwechsel. Seitenwechsel innerhalb
    // des Moduls laufen über onNavigate (Effekt unten) und behalten den Zustand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.id, geladen.url, basis, vorschauAn, services.activeTenant.id]);

  useEffect(() => {
    if (!ziel || angewandt.current === ziel.nr) return;
    angewandt.current = ziel.nr;
    springen(ziel.pfad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ziel?.nr]);

  if (error) {
    return (
      <Notice
        tone="danger"
        title={`${vorschauAn ? 'Vorschau von ' : 'Modul '}„${entry.title}" konnte nicht geladen werden.`}
      >
        {error} — Der Rest der Plattform läuft weiter, das Plattform-Team ist über die Telemetrie
        informiert.
      </Notice>
    );
  }
  return (
    <>
      {vorschau && vorschauAn && (
        <Notice
          tone="warn"
          title={`Vorschau – nicht freigegeben, arbeitet auf echten Daten (${vorschau.version})`}
          action={
            vorschau.nurVorschau ? undefined : (
              <Button size="sm" variant="secondary" onClick={() => setVorschauAn(false)}>
                {umschalterText(vorschau, true)}
              </Button>
            )
          }
        >
          {bannerText(vorschau)}
        </Notice>
      )}
      {vorschau && !vorschauAn && (
        <Notice
          tone="info"
          title="Neue Version zum Testen"
          action={
            <Button size="sm" variant="secondary" onClick={() => setVorschauAn(true)}>
              {umschalterText(vorschau, false)}
            </Button>
          }
        >
          {vorschau.nurVorschau
            ? `Version ${vorschau.version} ist noch nicht freigegeben und nur als Vorschau da. Nur Testende sehen sie; sie arbeitet auf echten Daten.`
            : `Version ${vorschau.version} läuft als Vorschau neben der freigegebenen Version${entry.version ? ` ${entry.version}` : ''}. Nur Testende sehen sie; sie arbeitet auf echten Daten.`}
        </Notice>
      )}
      {neu && !vorschauAn && (
        <Notice
          tone="info"
          title="Neue Version – neu laden"
          action={
            <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
              Neu laden
            </Button>
          }
        >
          „{entry.title}“ läuft jetzt in Version {neu} (geöffnet: {entry.version}). Neu laden, damit
          Oberfläche und Backend zusammenpassen; nicht gespeicherte Eingaben gehen dabei verloren.
        </Notice>
      )}
      <div ref={containerRef} className="shell-modul" />
    </>
  );
}
